import React, { memo, useEffect, useState } from "react";

import { Tick } from "@components/icons/tick";
import type { IIconFiles } from "@components/icons/types";

export type IIconStyle =
  | "Broken"
  | "LineDuotone"
  | "Linear"
  | "Outline"
  | "Bold"
  | "BoldDuotone";

export type IIconBaseProps = {
  color?: string;
  size?: number;
  autoSize?: boolean;
  svgProps?: React.SVGProps<SVGSVGElement>;
  iconStyle?: IIconStyle;
} & Omit<React.HTMLProps<HTMLSpanElement>, "color" | "size">;

export type IIconName = IIconFiles | "Tick";

export type IIconProps = { name: IIconName } & IIconBaseProps;

// Object to cache the dynamically imported icons
const iconCache: Record<string, React.ElementType> = {
  // Preload Tick since it's static
  Tick,
};

export const Icon = memo(function Icon({ name, ...restProps }: IIconProps) {
  const [DynamicIcon, setDynamicIcon] = useState<React.ElementType | null>(
    () => iconCache[name] || null,
  );

  useEffect(() => {
    // Only attempt to fetch the icon if it's not already cached
    if (iconCache[name]) {
      setDynamicIcon(() => iconCache[name]);
    } else if (name === "Tick") {
      // This is redundant due to the initial cache but left for consistency
      iconCache[name] = Tick;
      setDynamicIcon(() => Tick);
    } else {
      import(`@/components/icons/rawIcons/${name}.tsx`)
        .then((module) => {
          // Cache the loaded icon
          iconCache[name] = module[name];
          setDynamicIcon(() => module[name]);
        })
        .catch(() => {});
    }
  }, [name]);

  if (!DynamicIcon) {
    // or some placeholder while loading
    return null;
  }

  return <DynamicIcon {...restProps} />;
});
